import type {
  Money,
  Movement,
  Quantity
} from '../shared/types';
import {
  EntityBase,
  EntityType,
  MovementSchema,
  MovementStatusEnum,
  MovementTypes
} from '../shared/types';
import z from 'zod';

const VERSION_ONE = 1;

/**
 * MovementEntity — Central event in the inventory system (Event Sourcing).
 *
 * MVP: Movements are created directly as COMPLETED with initiatedBy/completedBy = 'system'.
 * The approval workflow (PENDING → IN_PROGRESS → COMPLETED) and its associated methods
 * (approve, complete, cancel, fail) are preserved for Phase 2 but not invoked by
 * current use cases. See: specs/reviews/2026-02-26_business-review.md § INC-02.
 */
export class MovementEntity {

  private constructor(private readonly props: Movement) { }

  static create(props: Omit<Movement, keyof z.infer<typeof EntityBase>>): MovementEntity {
    const now = new Date();
    const movement = MovementSchema.parse({
      ...props,
      _id: EntityType.enum.movement + globalThis.crypto.randomUUID(),
      type: EntityType.enum.movement,
      version: VERSION_ONE,
      createdAt: now,
      updatedAt: now,
    });
    return new MovementEntity(movement);
  }

  static restore(props: Movement): MovementEntity {
    const movement = MovementSchema.parse(props);
    return new MovementEntity(movement);
  }

  get _id(): string {
    return this.props._id;
  }

  get type(): keyof typeof MovementTypes {
    return this.props.movementType;
  }

  get version(): number {
    return this.props.version;
  }

  get movementType(): string {
    return this.props.movementType;
  }

  get productId(): string {
    return this.props.productId;
  }

  get warehouseId(): string {
    return this.props.warehouseId;
  }

  get quantity(): Quantity {
    return this.props.quantity;
  }

  get unitCost(): Money {
    return this.props.unitCost;
  }

  get status(): string {
    return this.props.status;
  }

  get unitPrice(): Money | undefined {
    return this.props.unitPrice;
  }

  get reason(): string | undefined {
    return this.props.reason;
  }

  get initiatedBy(): string {
    return this.props.initiatedBy;
  }

  get approvedBy(): string | undefined {
    return this.props.approvedBy;
  }

  get completedBy(): string | undefined {
    return this.props.completedBy;
  }

  get cancelledBy(): string | undefined {
    return this.props.cancelledBy;
  }

  get failedBy(): string | undefined {
    return this.props.failedBy;
  }

  get fromLocationId(): string | undefined {
    return this.props.fromLocationId;
  }

  get toLocationId(): string | undefined {
    return this.props.toLocationId;
  }

  get reference(): { type: 'PO' | 'SO' | 'TO' | 'ADJ'; number: string; externalId?: string } | undefined {
    return this.props.reference;
  }

  get notes(): string | undefined {
    return this.props.notes;
  }

  get destinationWarehouseId(): string | undefined {
    return this.props.destinationWarehouseId;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get updatedAt(): Date {
    return this.props.updatedAt;
  }

  set status(status: string) {
    this.props.status = MovementStatusEnum.parse(status);
  }

  set updatedAt(date: Date) {
    this.props.updatedAt = date;
  }

  set reason(reason: string | undefined) {
    this.props.reason = reason;
  }

  isReceiving(): boolean {
    return this.type === MovementTypes.IN;
  }

  isTransfer(): boolean {
    return this.type === MovementTypes.TRANSFER;
  }

  isPicking(): boolean {
    return this.type === MovementTypes.OUT;
  }

  isAdjustment(): boolean {
    return this.type === MovementTypes.ADJUSTMENT;
  }

  // These methods are deprecated as they don't match with the new movement types
  isReturn(): boolean {
    return false;
  }

  isScrap(): boolean {
    return false;
  }

  isPending(): boolean {
    return this.status === MovementStatusEnum.enum.PENDING;
  }

  isInProgress(): boolean {
    return this.status === MovementStatusEnum.enum.IN_PROGRESS;
  }

  isCompleted(): boolean {
    return this.status === MovementStatusEnum.enum.COMPLETED;
  }

  isCancelled(): boolean {
    return this.status === MovementStatusEnum.enum.CANCELLED;
  }

  isFailed(): boolean {
    return this.status === MovementStatusEnum.enum.FAILED;
  }

  approve(userId: string): void {
    if (!this.isPending()) {
      throw new Error('Movement can only be approved when pending');
    }
    this.props.approvedBy = userId;
    this.status = MovementStatusEnum.enum.IN_PROGRESS;
    this.updatedAt = new Date();
  }

  complete(userId: string): void {
    if (!this.isInProgress()) {
      throw new Error('Movement can only be completed when in progress');
    }
    this.props.completedBy = userId;
    this.status = MovementStatusEnum.enum.COMPLETED;
    this.updatedAt = new Date();
  }

  cancel(reason?: string): void {
    if (this.isCompleted()) {
      throw new Error('Cannot cancel a completed movement');
    }
    this.status = MovementStatusEnum.enum.CANCELLED;
    if (reason) {
      this.reason = reason;
    }
    this.updatedAt = new Date();
  }

  fail(reason: string): void {
    if (this.isCompleted() || this.isCancelled()) {
      throw new Error('Cannot fail a completed or cancelled movement');
    }
    this.status = MovementStatusEnum.enum.FAILED;
    this.reason = reason;
    this.updatedAt = new Date();
  }

  validate(): boolean {
    try {
      MovementSchema.parse(this.props);

      // Additional validation rules
      if (this.props.movementType === MovementTypes.TRANSFER && (!this.props.fromLocationId || !this.props.toLocationId)) {
        return false;
      }
      if (this.props.movementType === MovementTypes.IN && !this.props.toLocationId) {
        return false;
      }
      if (this.props.movementType === MovementTypes.OUT && !this.props.fromLocationId) {
        return false;
      }
      if (this.isCompleted() && !this.props.completedBy) {
        return false;
      }
      if (this.isInProgress() && !this.props.approvedBy) {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  toJSON(): Movement {
    return { ...this.props };
  }
}

export type { Movement };
